// client/src/components/AdminUsers.js

import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { firestore, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  documentId,
  getCountFromServer,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "../CSS/Dashboard.css";
import AdminNavbar from "./AdminNavbar";

const PAGE_SIZE = 50; // ajuste 25/50/100 selon trafic/budget

const ROLE_OPTIONS = [
  { label: "Tout", value: "Tout" },
  { label: "Commerçants en attente", value: "commercantpending" },
  { label: "Commerçant", value: "commercant" },
  { label: "Clients", value: "client" },
  { label: "Caisses", value: "cashier" },
  { label: "Affiliates en attente", value: "affiliatepending" },
  { label: "Affiliates", value: "affiliates" },
  { label: "Régie Affiliates", value: "AffiliateRegie" },
  { label: "Admin", value: "admin" },
];

/**
 * OPTIS COÛTS (NO-INDEX)
 * - Pagination stricte (limit + startAfter).
 * - Cache mémoire par filtre.
 * - getCountFromServer pour le total (sans télécharger les docs).
 * - Tri serveur **uniquement** par documentId() (aucun index composite requis).
 */

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("Tout");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Pagination
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  // Cache { [filter]: { pages: [[...]], lastDoc, hasMore, totalCount } }
  const cacheRef = useRef({});
  const searchRunIdRef = useRef(0);

  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  // ==== Helpers de requête Firestore (NO INDEX) ====

  // Construit la requête : where(role==filter) si filtré, tri **toujours** par documentId() (aucun composite)
  const buildQuery = useCallback(
    (cursor = null) => {
      const baseRef = collection(firestore, "users");
      const parts = [];

      if (filter !== "Tout") {
        parts.push(where("role", "==", filter));
      }

      // Tri stable sans index composite
      parts.push(orderBy(documentId()));

      parts.push(limit(PAGE_SIZE));
      if (cursor) {
        parts.push(startAfter(cursor));
      }
      return query(baseRef, ...parts);
    },
    [filter]
  );

  // Total server-side (léger) — ne nécessite pas d'index composite
  const fetchTotalCount = useCallback(async () => {
    try {
      const usersCol = collection(firestore, "users");
      const q =
        filter === "Tout"
          ? usersCol
          : query(usersCol, where("role", "==", filter));
      const snap = await getCountFromServer(q);
      return snap.data().count || 0;
    } catch (e) {
      console.warn("getCountFromServer failed, totalCount inconnu.", e);
      return null;
    }
  }, [filter]);

  // ==== Lecture première page (avec cache + count server-side) ====
  useEffect(() => {
    let cancelled = false;

    const loadFirstPage = async () => {
      setLoading(true);
      setMessage("");
      setUsers([]);
      setHasMore(true);
      setLastDoc(null);
      setTotalCount(null);

      // 1) Cache existant ?
      const cached = cacheRef.current[filter];
      if (cached && cached.pages?.length) {
        if (!cancelled) {
          setUsers(cached.pages.flat());
          setLastDoc(cached.lastDoc || null);
          setHasMore(cached.hasMore ?? true);
          setTotalCount(cached.totalCount ?? null);
          setLoading(false);
        }
        return;
      }

      try {
        // 2) Total (léger)
        const total = await fetchTotalCount();
        if (!cancelled) setTotalCount(total);

        // 3) Première page (NO-INDEX)
        const snap = await getDocs(buildQuery(null));

        if (cancelled) return;

        const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const last = snap.docs[snap.docs.length - 1] || null;

        const computedHasMore =
          typeof total === "number"
            ? list.length < total
            : snap.docs.length === PAGE_SIZE;

        setUsers(list);
        setLastDoc(last);
        setHasMore(computedHasMore);

        cacheRef.current[filter] = {
          pages: [list],
          lastDoc: last,
          hasMore: computedHasMore,
          totalCount: total,
        };
      } catch (err) {
        console.error(err);
        if (!cancelled) setMessage("Erreur lors de la récupération des utilisateurs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFirstPage();
    return () => {
      cancelled = true;
    };
  }, [buildQuery, fetchTotalCount, filter]);

  // ==== Charger plus (respecte le cache) ====
  const loadMore = async () => {
    if (!hasMore || fetchingMore) return;

    setFetchingMore(true);
    setMessage("");

    try {
      const cached = cacheRef.current[filter];

      const q = buildQuery(lastDoc);
      const snap = await getDocs(q);
      const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      const nextLast = snap.docs[snap.docs.length - 1] || null;

      setUsers((prev) => [...prev, ...list]);
      setLastDoc(nextLast);

      const total = typeof totalCount === "number" ? totalCount : cached?.totalCount ?? null;
      const alreadyLoaded = (cached?.pages?.flat()?.length || 0);
      const newAll = alreadyLoaded + list.length;

      const computedHasMore =
        typeof total === "number" ? newAll < total : snap.docs.length === PAGE_SIZE;

      setHasMore(computedHasMore);

      const newPages = cached?.pages ? [...cached.pages, list] : [list];
      cacheRef.current[filter] = {
        pages: newPages,
        lastDoc: nextLast,
        hasMore: computedHasMore,
        totalCount: total,
      };
    } catch (err) {
      console.error(err);
      setMessage("Erreur lors du chargement supplémentaire");
    } finally {
      setFetchingMore(false);
    }
  };

  // ==== Gestion du filtre ====
  const handleFilterChange = (e) => {
    setFilter(e.target.value);
  };

  // Recherche locale (et progressive)
  const [search, setSearch] = useState("");
  const displayedUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase().trim();
    return users.filter(u =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.pseudo || "").toLowerCase().includes(q) ||
      (u.id || "").toLowerCase().includes(q)
    );
  }, [users, search]);

  // Recherche : charge automatiquement toutes les pages nécessaires pour couvrir le terme saisi.
  useEffect(() => {
    if (loading) return;
    const term = search.trim();
    if (!term) {
      setSearching(false);
      return;
    }

    const runId = ++searchRunIdRef.current;
    let cancelled = false;

    const searchMore = async () => {
      const cached = cacheRef.current[filter];
      let pages = cached?.pages || (users.length ? [users] : []);
      let cursor = cached?.lastDoc || lastDoc;
      let localHasMore = cached?.hasMore ?? hasMore;
      let total =
        typeof totalCount === "number"
          ? totalCount
          : cached?.totalCount ?? null;

      setFetchingMore(true);
      try {
        while (
          !cancelled &&
          searchRunIdRef.current === runId &&
          localHasMore
        ) {
          const snap = await getDocs(buildQuery(cursor));
          if (cancelled || searchRunIdRef.current !== runId) break;

          const list = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
          const nextLast = snap.docs[snap.docs.length - 1] || null;

          pages = [...pages, list];
          const loadedCount = pages.reduce((acc, p) => acc + p.length, 0);

          const computedHasMore =
            typeof total === "number"
              ? loadedCount < total
              : snap.docs.length === PAGE_SIZE;

          cacheRef.current[filter] = {
            pages,
            lastDoc: nextLast,
            hasMore: computedHasMore,
            totalCount: total,
          };

          setUsers(pages.flat());
          setLastDoc(nextLast);
          setHasMore(computedHasMore);

          cursor = nextLast;
          localHasMore = computedHasMore;
        }
      } catch (err) {
        console.error(err);
        setMessage("Erreur lors de la recherche");
      } finally {
        setFetchingMore(false);
        if (!cancelled && searchRunIdRef.current === runId) {
          setSearching(false);
        }
      }
    };

    const timer = setTimeout(() => {
      setSearching(true);
      searchMore();
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter, loading, hasMore, lastDoc, buildQuery, totalCount]);

  const handleVoirPlus = (user) => {
    navigate("/adminusersviewer", { state: { user } });
  };

  const roleLabel = (r) => (ROLE_OPTIONS.find((o) => o.value === r)?.label) || r || "—";
  const roleBadgeClass = (r) => {
    if (r === "commercantpending" || r === "affiliatepending") return "eb-badge eb-badge--warning";
    if (r === "admin") return "eb-badge eb-badge--danger";
    if (r === "commercant" || r === "affiliates" || r === "AffiliateRegie") return "eb-badge eb-badge--success";
    return "eb-badge";
  };

  return (
    <div className="dashboard-container">
      <style>{`
        .au-page { width: 100%; display: flex; flex-direction: column; gap: 16px; }
        .au-page .eb-page-header { margin-bottom: 8px; }
        .dashboard-container .au-page .eb-page-title { margin: 0; text-align: left; }
        .au-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .au-search { position: relative; flex: 1 1 260px; min-width: 0; }
        .au-search svg { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); width: 16px; height: 16px; color: var(--eb-subtle); pointer-events: none; }
        .au-search .eb-input { padding-left: 36px; min-height: 42px; font-size: 14.5px; }
        .au-select { min-height: 42px; padding: 0 12px; font-size: 14px; border-radius: var(--eb-radius-sm); background: var(--eb-surface); cursor: pointer; max-width: 100%; }
        .au-table-card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); overflow: hidden; }
        .au-table-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .au-table-card .dashboard-table { margin: 0; border: 0; border-radius: 0; box-shadow: none; min-width: 640px; width: 100%; max-width: none; table-layout: auto; }
        .au-table-card .dashboard-table th, .au-table-card .dashboard-table td { text-align: left; padding: 12px 16px; }
        .au-table-card .dashboard-table td { font-size: 14px; vertical-align: middle; }
        .au-uid { font-family: var(--eb-font-mono); font-size: 12.5px !important; color: var(--eb-muted); word-break: break-all; }
        .au-email { color: var(--eb-text); font-weight: 500; overflow-wrap: anywhere; }
        .au-anon { color: var(--eb-subtle); font-style: italic; }
        .au-denied { font-size: 13px; color: var(--eb-muted); }
        .au-actions-col { text-align: right !important; white-space: nowrap; }
        .au-status { display: flex; align-items: center; gap: 8px; color: var(--eb-muted); font-size: 13.5px; margin: 0; }
        .au-empty { padding: 32px 16px; text-align: center; color: var(--eb-muted); font-size: 14px; margin: 0; }
        .au-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .au-footer p { margin: 0; color: var(--eb-muted); font-size: 13.5px; }
        @media (max-width: 600px) {
          .au-search { flex-basis: 100%; }
          .au-select { flex: 1 1 100%; }
          .au-footer .eb-btn { width: 100%; }
        }
      `}</style>
      <AdminNavbar />

      <div className="au-page">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Utilisateurs</h1>
            <p className="eb-page-subtitle">Recherchez et consultez les comptes de la plateforme</p>
          </div>
          {typeof totalCount === "number" && (
            <span className="eb-badge">Total : {totalCount}</span>
          )}
        </header>

        <div className="au-toolbar">
          <div className="au-search">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              className="eb-input"
              placeholder="Rechercher (email, pseudo, UID)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            id="filter-select"
            className="au-select"
            aria-label="Filtrer par rôle"
            value={filter}
            onChange={handleFilterChange}
          >
            {ROLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {message && <div className="eb-alert eb-alert--danger">{message}</div>}
        {searching && search.trim() && !loading && (
          <p className="au-status"><span className="eb-spinner eb-spinner--sm" /> Recherche dans les autres pages…</p>
        )}

        {loading ? (
          <div className="au-table-card">
            <p className="au-empty" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
              <span className="eb-spinner eb-spinner--sm" /> Chargement des utilisateurs...
            </p>
          </div>
        ) : (
          <>
            <div className="au-table-card">
              <div className="au-table-scroll">
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Pseudo</th>
                      <th>Rôle</th>
                      <th>UID</th>
                      <th className="au-actions-col">Voir plus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedUsers.map((user) => (
                      <tr key={user.id}>
                        <td>{user.email ? <span className="au-email">{user.email}</span> : <span className="au-anon">Anonyme</span>}</td>
                        <td>{user.pseudo}</td>
                        <td><span className={roleBadgeClass(user.role)}>{roleLabel(user.role)}</span></td>
                        <td className="au-uid">{user.id}</td>
                        <td className="au-actions-col">
                          {user.role === "admin" ? (
                            currentUser &&
                            currentUser.uid === "uA4O9KqZrbdsv9JNaSITwULDwqJ2" ? (
                              <button className="eb-btn eb-btn--secondary eb-btn--sm" onClick={() => handleVoirPlus(user)}>
                                Voir plus
                              </button>
                            ) : (
                              <span className="au-denied">Accès refusé</span>
                            )
                          ) : (
                            <button className="eb-btn eb-btn--secondary eb-btn--sm" onClick={() => handleVoirPlus(user)}>
                              Voir plus
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {displayedUsers.length === 0 && !search.trim() && (
                <p className="au-empty">Aucun utilisateur.</p>
              )}
              {!searching && search.trim() && displayedUsers.length === 0 && (
                <p className="au-empty">
                  Aucun résultat pour « {search} »
                </p>
              )}
            </div>

            <div className="au-footer">
              {!hasMore && displayedUsers.length > 0 ? (
                <p>
                  Tous les résultats ont été chargés
                  {typeof totalCount === "number" ? ` (${displayedUsers.length}/${totalCount})` : "."}
                </p>
              ) : <span />}
              {hasMore && !search.trim() && (
                <button className="eb-btn eb-btn--secondary" onClick={loadMore} disabled={fetchingMore}>
                  {fetchingMore ? "Chargement..." : "Charger plus"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
